import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ReceiptText, TrendingUp, WalletCards } from 'lucide-react';
import AudioPlayButton from '../AudioPlayButton';
import { Audio, Course, UserMessage, Video, Workshop } from '../../types';
import { formatPersianDateWithTime } from '../../utils/dateUtils';
import { getImageUrl } from '../../utils/imageUtils';
import { getCoursePathSegment } from '../../utils/coursePath';
import { DashboardCard, DashboardEmptyState, DashboardSectionTitle } from './DashboardShared';

function getCourseProgress(courseId: string, courseAudios: Audio[]): number {
  if (!courseAudios.length) return 0;
  let started = 0;
  for (const a of courseAudios) {
    try {
      const saved = localStorage.getItem(`audio_progress_${a.id}`);
      if (saved && parseFloat(saved) > 3) started++;
    } catch { /* ignore */ }
  }
  return Math.round((started / courseAudios.length) * 100);
}

function getResumeUrl(course: Course, courseAudios: Audio[]): string {
  const base = `/courses/${getCoursePathSegment(course)}`;
  if (!courseAudios.length) return base;
  try {
    const lastId = localStorage.getItem(`last_audio_${course.id}`);
    if (lastId && courseAudios.some((a) => a.id === lastId)) {
      return `${base}/audios/${lastId}`;
    }
  } catch { /* ignore */ }
  return `${base}/audios/${courseAudios[0].id}`;
}

const imageFallback = 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?q=80&w=900&auto=format&fit=crop';

const Thumb: React.FC<{ src?: string; alt: string }> = ({ src, alt }) => {
  const resolved = src ? getImageUrl(src) || imageFallback : imageFallback;
  return <img src={resolved} alt={alt} className="h-28 w-full rounded-xl object-cover sm:h-32" />;
};

const CircularProgress: React.FC<{ value: number }> = ({ value }) => {
  const normalized = Math.max(0, Math.min(100, value));
  const angle = (normalized / 100) * 360;
  return (
    <div
      className="relative h-16 w-16 rounded-full"
      style={{
        background: `conic-gradient(#1C3B9B ${angle}deg, #DFE7FF ${angle}deg 360deg)`,
      }}
    >
      <div className="absolute inset-[6px] flex items-center justify-center rounded-full bg-white">
        <span className="text-xs font-black text-[#1C3B9B]">{normalized.toLocaleString('fa-IR')}٪</span>
      </div>
    </div>
  );
};

export const DashboardCoursesPanel: React.FC<{ courses: Course[]; audios: Audio[] }> = ({ courses, audios }) => {
  const audiosByCourse = useMemo(() => {
    const map = new Map<string, Audio[]>();
    for (const a of audios) {
      if (!map.has(a.courseId)) map.set(a.courseId, []);
      map.get(a.courseId)!.push(a);
    }
    return map;
  }, [audios]);

  if (!courses.length) {
    return <DashboardEmptyState title="هنوز دوره‌ای فعال نشده" subtitle="پس از خرید دوره‌ها، اینجا نمایش داده می‌شوند." action={<Link to="/courses" className="rounded-xl bg-[#1C3B9B] px-4 py-2 text-sm font-bold text-white shadow-[0_10px_22px_-14px_rgba(28,59,155,0.6)]">مشاهده دوره‌ها</Link>} />;
  }

  const discountedCount = courses.filter((c) => Number(c.listPrice || c.price || 0) > Number(c.price || 0)).length;

  return (
    <div className="space-y-4">
      <div className="rounded-3xl border border-[#DDE5FF] bg-[linear-gradient(145deg,#FFFFFF_0%,#F7FAFF_100%)] p-4 shadow-[0_14px_30px_-20px_rgba(28,59,155,0.35)] sm:p-5">
        <DashboardSectionTitle
          title="دوره‌های من"
          subtitle="لیست دوره‌های فعال با زبان بصری هم‌تم صفحه دوره"
          accent="violet"
          action={
            <Link
              to="/courses"
              className="inline-flex items-center rounded-xl border border-[#C8D6FF] bg-white px-3 py-2 text-xs font-black text-[#1C3B9B] transition hover:-translate-y-0.5 hover:shadow-[0_12px_24px_-16px_rgba(28,59,155,0.45)]"
            >
              مشاهده همه دوره‌ها
            </Link>
          }
        />
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="rounded-2xl border border-[#DDE5FF] bg-white px-3 py-3 shadow-[0_8px_20px_-16px_rgba(15,23,42,0.45)]">
            <p className="text-[11px] font-bold text-slate-500">تعداد دوره‌ها</p>
            <p className="mt-1 text-lg font-black text-slate-900">{courses.length.toLocaleString('fa-IR')}</p>
          </div>
          <div className="rounded-2xl border border-[#DDE5FF] bg-white px-3 py-3 shadow-[0_8px_20px_-16px_rgba(15,23,42,0.45)]">
            <p className="text-[11px] font-bold text-slate-500">دارای تخفیف فعال</p>
            <p className="mt-1 text-lg font-black text-[#1C3B9B]">{discountedCount.toLocaleString('fa-IR')}</p>
          </div>
          <div className="rounded-2xl border border-[#DDE5FF] bg-white px-3 py-3 shadow-[0_8px_20px_-16px_rgba(15,23,42,0.45)]">
            <p className="text-[11px] font-bold text-slate-500">وضعیت دسترسی</p>
            <p className="mt-1 text-lg font-black text-emerald-700">فعال</p>
          </div>
        </div>
      </div>
      <div className="space-y-3">
        {courses.map((course) => {
          const courseAudios = audiosByCourse.get(course.id) ?? [];
          const progress = getCourseProgress(course.id, courseAudios);
          const resumeUrl = getResumeUrl(course, courseAudios);
          return (
          <DashboardCard
            key={course.id}
            className="group border-[#DDE5FF] bg-[linear-gradient(180deg,#FFFFFF_0%,#FAFCFF_100%)] p-3.5 transition hover:border-[#C8D6FF] hover:shadow-[0_18px_30px_-22px_rgba(28,59,155,0.45)] sm:p-4.5"
          >
            <div className="flex flex-col gap-3.5 sm:flex-row sm:items-center">
              <div className="relative w-full overflow-hidden rounded-xl border border-[#DDE5FF] sm:w-60">
                <Thumb src={course.thumbnail} alt={course.title} />
                <span className="absolute right-2 top-2 rounded-full border border-[#C8D6FF] bg-white/95 px-2.5 py-1 text-[10px] font-black text-[#1C3B9B] shadow-[0_8px_18px_-14px_rgba(28,59,155,0.45)]">
                  دوره فعال
                </span>
              </div>
              <div className="flex flex-1 flex-col gap-3">
                <p className="line-clamp-2 text-[15px] font-black leading-7 text-slate-900">{course.title}</p>
                <div className="flex flex-wrap items-center gap-4">
                  <div className="rounded-lg border border-[#E6ECFF] bg-white px-2.5 py-1.5 text-xs text-slate-500">
                    <p>قیمت دوره</p>
                    <p className="mt-0.5 font-black text-slate-900">{Number(course.price || 0).toLocaleString('fa-IR')} تومان</p>
                  </div>
                  <div className="rounded-lg border border-[#E6ECFF] bg-white px-2.5 py-1.5 text-xs text-slate-500">
                    <p>نوع دسترسی</p>
                    <p className="mt-0.5 font-black text-emerald-700">فعال</p>
                  </div>
                  {courseAudios.length > 0 && (
                    <div className="rounded-lg border border-[#E6ECFF] bg-white px-2.5 py-1.5 text-xs text-slate-500">
                      <p>فایل‌های صوتی</p>
                      <p className="mt-0.5 font-black text-slate-900">{courseAudios.length.toLocaleString('fa-IR')} جلسه</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between gap-4 sm:block sm:text-center">
                <CircularProgress value={progress} />
                <p className="mt-2 text-[11px] font-bold text-slate-500">تکمیل دوره</p>
              </div>
            </div>
            <div className="mt-3.5 flex flex-wrap items-center gap-2">
              <Link
                to={resumeUrl}
                className="inline-flex rounded-lg bg-[#1C3B9B] px-4 py-2 text-xs font-black text-white shadow-[0_10px_22px_-14px_rgba(28,59,155,0.65)] transition hover:bg-[#173280]"
              >
                ادامه یادگیری
              </Link>
              <Link
                to={`/courses/${getCoursePathSegment(course)}`}
                className="inline-flex rounded-lg border border-[#C8D6FF] bg-white px-4 py-2 text-xs font-black text-[#1C3B9B] transition hover:bg-[#EEF4FF]"
              >
                جزئیات دوره
              </Link>
            </div>
          </DashboardCard>
          );
        })}
      </div>
    </div>
  );
};

export const DashboardVideosPanel: React.FC<{ videos: Video[] }> = ({ videos }) => {
  if (!videos.length) return <DashboardEmptyState title="ویدیویی در دسترس نیست" subtitle="ویدیوهای دوره‌های شما در این بخش نمایش داده می‌شود." />;
  return (
    <div className="space-y-4">
      <div className="rounded-3xl border border-[#DDE5FF] bg-[linear-gradient(145deg,#FFFFFF_0%,#F7FAFF_100%)] p-4 shadow-[0_14px_30px_-20px_rgba(28,59,155,0.35)] sm:p-5">
        <DashboardSectionTitle title="ویدیوهای من" subtitle="دسترسی سریع به جلسات ویدیویی دوره‌ها" accent="violet" />
      </div>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {videos.map((video) => (
          <DashboardCard key={video.id} className="space-y-3 border-[#DDE5FF] bg-[linear-gradient(180deg,#FFFFFF_0%,#FAFCFF_100%)] transition hover:border-[#C8D6FF] hover:shadow-[0_18px_30px_-22px_rgba(28,59,155,0.45)]">
            <Thumb src={video.thumbnail} alt={video.title} />
            <p className="line-clamp-2 text-sm font-black text-slate-900">{video.title}</p>
            <Link to={`/video/${video.id}`} className="inline-flex rounded-lg bg-[#1C3B9B] px-3 py-2 text-xs font-bold text-white">پخش ویدیو</Link>
          </DashboardCard>
        ))}
      </div>
    </div>
  );
};

export const DashboardAudiosPanel: React.FC<{ audios: Audio[] }> = ({ audios }) => {
  if (!audios.length) return <DashboardEmptyState title="فایل صوتی یافت نشد" subtitle="فایل‌های صوتی دوره‌ها از اینجا قابل پخش هستند." />;
  return (
    <div className="space-y-4">
      <div className="rounded-3xl border border-[#DDE5FF] bg-[linear-gradient(145deg,#FFFFFF_0%,#F7FAFF_100%)] p-4 shadow-[0_14px_30px_-20px_rgba(28,59,155,0.35)] sm:p-5">
        <DashboardSectionTitle title="فایل‌های صوتی من" subtitle="کتابخانه صوتی با دسترسی سریع و حرفه‌ای" accent="violet" />
      </div>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {audios.map((audio) => (
          <DashboardCard key={audio.id} className="space-y-3 border-[#DDE5FF] bg-[linear-gradient(180deg,#FFFFFF_0%,#FAFCFF_100%)] transition hover:border-[#C8D6FF] hover:shadow-[0_18px_30px_-22px_rgba(28,59,155,0.45)]">
            <Thumb src={audio.thumbnail} alt={audio.title} />
            <p className="line-clamp-2 text-sm font-black text-slate-900">{audio.title}</p>
            <AudioPlayButton audio={audio} />
          </DashboardCard>
        ))}
      </div>
    </div>
  );
};

export const DashboardWorkshopsPanel: React.FC<{ workshops: Workshop[] }> = ({ workshops }) => {
  if (!workshops.length) return <DashboardEmptyState title="کارگاهی ثبت نشده" subtitle="کارگاه‌های ثبت‌نامی شما در این بخش نمایش داده می‌شوند." />;
  return (
    <div className="space-y-4">
      <div className="rounded-3xl border border-[#DDE5FF] bg-[linear-gradient(145deg,#FFFFFF_0%,#F7FAFF_100%)] p-4 shadow-[0_14px_30px_-20px_rgba(28,59,155,0.35)] sm:p-5">
        <DashboardSectionTitle title="کارگاه‌های من" subtitle="رویدادهای ثبت‌نام‌شده با نمای مدرن و خلاصه تاریخ" accent="violet" />
      </div>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {workshops.map((workshop) => (
          <DashboardCard key={workshop.id} className="space-y-3 border-[#DDE5FF] bg-[linear-gradient(180deg,#FFFFFF_0%,#FAFCFF_100%)] transition hover:border-[#C8D6FF] hover:shadow-[0_18px_30px_-22px_rgba(28,59,155,0.45)]">
            <Thumb src={workshop.thumbnail} alt={workshop.title} />
            <p className="line-clamp-2 text-sm font-black text-slate-900">{workshop.title}</p>
            <p className="text-xs text-slate-500">{formatPersianDateWithTime(workshop.date)}</p>
            <Link to={`/workshops/${workshop.id}`} className="inline-flex rounded-lg bg-[#1C3B9B] px-3 py-2 text-xs font-bold text-white">جزئیات کارگاه</Link>
          </DashboardCard>
        ))}
      </div>
    </div>
  );
};

export const DashboardMessagesPanel: React.FC<{ messages: UserMessage[]; onMarkRead: (id: string) => void }> = ({ messages, onMarkRead }) => {
  if (!messages.length) return <DashboardEmptyState title="پیامی ندارید" subtitle="پیام‌های جدید و اطلاع‌رسانی‌ها در اینجا قرار می‌گیرند." />;
  return (
    <div className="space-y-4">
      <div className="rounded-3xl border border-[#DDE5FF] bg-[linear-gradient(145deg,#FFFFFF_0%,#F7FAFF_100%)] p-4 shadow-[0_14px_30px_-20px_rgba(28,59,155,0.35)] sm:p-5">
        <DashboardSectionTitle title="پیام‌های من" subtitle="صندوق پیام حرفه‌ای با تفکیک خوانده‌نشده" accent="violet" />
      </div>
      <div className="space-y-3">
        {messages.map((item) => (
          <DashboardCard key={item.id} className={`border-[#DDE5FF] ${item.isRead ? 'bg-white' : 'bg-[#EEF4FF]'}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-sm font-black text-slate-900">{item.message?.title || 'پیام'}</p>
                <p className="mt-1 text-xs text-slate-500">{formatPersianDateWithTime(item.createdAt)}</p>
              </div>
              {!item.isRead ? (
                <button type="button" onClick={() => onMarkRead(item.id)} className="rounded-lg bg-[#1C3B9B] px-3 py-2 text-xs font-bold text-white">
                  علامت‌گذاری به عنوان خوانده‌شده
                </button>
              ) : null}
            </div>
            <p className="mt-3 whitespace-pre-wrap text-sm text-slate-700">{item.message?.body || '-'}</p>
          </DashboardCard>
        ))}
      </div>
    </div>
  );
};

export const DashboardWalletPanel: React.FC<{ walletBalance: number; invoices: any[] }> = ({ walletBalance, invoices }) => {
  const safeInvoices = Array.isArray(invoices) ? invoices : [];
  const paidCount = safeInvoices.filter((invoice) => invoice?.status === 'PAID').length;
  const inCartCount = safeInvoices.filter((invoice) => invoice?.status === 'PENDING').length;
  const totalSpent = safeInvoices
    .filter((invoice) => invoice?.status === 'PAID')
    .reduce((sum, invoice) => sum + Number(invoice?.amount || 0), 0);

  const resolveStatusMeta = (status?: string) => {
    if (status === 'PAID') {
      return { label: 'تکمیل شده', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
    }
    if (status === 'PENDING') {
      return { label: 'افزوده به سبد خرید', className: 'bg-amber-50 text-amber-700 border-amber-200' };
    }
    return { label: status || '-', className: 'bg-slate-100 text-slate-600 border-slate-200' };
  };

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-3xl border border-[#D8CCFF] bg-[linear-gradient(145deg,#FFFFFF_0%,#F8F4FF_42%,#F3F7FF_100%)] p-4 shadow-[0_22px_46px_-30px_rgba(124,58,237,0.45)] sm:p-5">
        <div className="pointer-events-none absolute" />
        <DashboardSectionTitle
          title="کیف پول و فاکتورها"
          subtitle="نمای یکپارچه موجودی، سفارش‌ها و هزینه‌های انجام‌شده"
          accent="violet"
          action={
            <div className="flex items-center gap-2">
              <Link to="/cart" className="rounded-lg border border-[#D8CCFF] bg-white px-3 py-1.5 text-xs font-black text-[#6d28d9] transition hover:bg-[#F5F0FF]">
                افزایش موجودی
              </Link>
              <Link to="/dashboard?tab=orders" className="rounded-lg border border-[#C8D6FF] bg-white px-3 py-1.5 text-xs font-black text-[#1C3B9B] transition hover:bg-[#EEF4FF]">
                مشاهده سفارش‌ها
              </Link>
            </div>
          }
        />

        <div className="grid gap-3 sm:grid-cols-3">
          <div className="rounded-2xl border border-[#DDD6FE] bg-white px-3 py-3 shadow-[0_10px_26px_-20px_rgba(91,33,182,0.55)]">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-bold text-slate-500">اعتبار فعلی کیف پول</p>
              <span className="rounded-lg bg-[#F2EFFF] p-1.5 text-[#6d28d9]">
                <WalletCards className="h-3.5 w-3.5" />
              </span>
            </div>
            <p className="mt-1.5 text-xl font-black text-[#6d28d9]">{walletBalance.toLocaleString('fa-IR')} تومان</p>
          </div>
          <div className="rounded-2xl border border-[#DDE5FF] bg-white px-3 py-3 shadow-[0_10px_26px_-20px_rgba(28,59,155,0.5)]">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-bold text-slate-500">مجموع پرداخت موفق</p>
              <span className="rounded-lg bg-[#EEF4FF] p-1.5 text-[#1C3B9B]">
                <TrendingUp className="h-3.5 w-3.5" />
              </span>
            </div>
            <p className="mt-1 text-lg font-black text-[#1C3B9B]">{totalSpent.toLocaleString('fa-IR')} تومان</p>
          </div>
          <div className="rounded-2xl border border-[#DDE5FF] bg-white px-3 py-3 shadow-[0_10px_26px_-20px_rgba(15,23,42,0.45)]">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-bold text-slate-500">وضعیت فاکتورها</p>
              <span className="rounded-lg bg-slate-100 p-1.5 text-slate-600">
                <ReceiptText className="h-3.5 w-3.5" />
              </span>
            </div>
            <p className="mt-1 text-sm font-black text-slate-900">
              <span className="text-emerald-700">{paidCount.toLocaleString('fa-IR')} تکمیل‌شده</span>
              <span className="mx-1.5 text-slate-300">•</span>
              <span className="text-amber-700">{inCartCount.toLocaleString('fa-IR')} در سبد</span>
            </p>
          </div>
        </div>
      </div>

      {safeInvoices.length ? (
        <div className="space-y-3">
          {safeInvoices.slice(0, 12).map((invoice: any) => {
            const statusMeta = resolveStatusMeta(invoice?.status);
            return (
              <DashboardCard
                key={invoice.id}
                className="border-[#DDE5FF] bg-[linear-gradient(180deg,#FFFFFF_0%,#FCFBFF_100%)] transition hover:border-[#D8CCFF] hover:shadow-[0_20px_34px_-26px_rgba(124,58,237,0.45)]"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-black text-slate-900">{invoice.course?.title || 'فاکتور خرید'}</p>
                    <p className="mt-1 text-xs text-slate-500">{formatPersianDateWithTime(invoice.createdAt)}</p>
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-black text-[#1C3B9B]">{Number(invoice.amount || 0).toLocaleString('fa-IR')} تومان</p>
                    <span className={`mt-1 inline-flex rounded-full border px-2 py-0.5 text-[11px] font-bold ${statusMeta.className}`}>
                      {statusMeta.label}
                    </span>
                  </div>
                </div>
              </DashboardCard>
            );
          })}
        </div>
      ) : (
        <DashboardEmptyState
          title="هنوز فاکتوری ثبت نشده"
          subtitle="بعد از خرید یا افزودن دوره به سبد، تاریخچه مالی اینجا نمایش داده می‌شود."
          action={
            <Link to="/courses" className="rounded-xl bg-[#1C3B9B] px-4 py-2 text-sm font-bold text-white shadow-[0_10px_22px_-14px_rgba(28,59,155,0.6)]">
              مشاهده دوره‌ها
            </Link>
          }
        />
      )}
    </div>
  );
};

export const DashboardOrdersPanel: React.FC<{ invoices: any[] }> = ({ invoices }) => {
  const orders = Array.isArray(invoices) ? invoices.filter((inv) => inv?.type === 'COURSE_PURCHASE' || inv?.courseId || inv?.course) : [];
  const paid = orders.filter((o) => o?.status === 'PAID').length;
  const pending = orders.filter((o) => o?.status === 'PENDING').length;
  const totalAmount = orders.reduce((sum, order) => sum + Number(order?.amount || 0), 0);

  const resolveOrderStatus = (status?: string) => {
    if (status === 'PAID') {
      return { label: 'تکمیل شده', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
    }
    if (status === 'PENDING') {
      return { label: 'افزوده به سبد خرید', className: 'bg-amber-50 text-amber-700 border-amber-200' };
    }
    return { label: status || '-', className: 'bg-slate-100 text-slate-600 border-slate-200' };
  };

  return (
    <div className="space-y-4">
      <div className="rounded-3xl border border-[#D8CCFF] bg-[linear-gradient(145deg,#FFFFFF_0%,#F8F4FF_42%,#F4F8FF_100%)] p-4 shadow-[0_22px_46px_-30px_rgba(124,58,237,0.42)] sm:p-5">
        <DashboardSectionTitle
          title="سفارش‌های من"
          subtitle="نمای هوشمند وضعیت سفارش‌ها و پرداخت‌های دوره‌ها"
          accent="violet"
          action={
            <Link to="/courses" className="rounded-lg border border-[#C8D6FF] bg-white px-3 py-1.5 text-xs font-black text-[#1C3B9B] transition hover:bg-[#EEF4FF]">
              افزودن دوره جدید
            </Link>
          }
        />
        <div className="grid gap-3 sm:grid-cols-4">
          <div className="rounded-2xl border border-[#DDE5FF] bg-white px-3 py-3 shadow-[0_10px_22px_-20px_rgba(15,23,42,0.45)]"><p className="text-[11px] text-slate-500">کل سفارش‌ها</p><p className="text-lg font-black">{orders.length.toLocaleString('fa-IR')}</p></div>
          <div className="rounded-2xl border border-[#DDE5FF] bg-white px-3 py-3 shadow-[0_10px_22px_-20px_rgba(22,163,74,0.45)]"><p className="text-[11px] text-slate-500">تکمیل‌شده</p><p className="text-lg font-black text-emerald-700">{paid.toLocaleString('fa-IR')}</p></div>
          <div className="rounded-2xl border border-[#DDE5FF] bg-white px-3 py-3 shadow-[0_10px_22px_-20px_rgba(217,119,6,0.45)]"><p className="text-[11px] text-slate-500">در سبد خرید</p><p className="text-lg font-black text-amber-700">{pending.toLocaleString('fa-IR')}</p></div>
          <div className="rounded-2xl border border-[#DDD6FE] bg-white px-3 py-3 shadow-[0_10px_22px_-20px_rgba(124,58,237,0.45)]"><p className="text-[11px] text-slate-500">جمع مبالغ</p><p className="text-lg font-black text-[#6d28d9]">{totalAmount.toLocaleString('fa-IR')} ت</p></div>
        </div>
      </div>
      {orders.length ? (
        <div className="space-y-3">
          {orders.map((order) => (
            <DashboardCard key={order.id} className="border-[#DDE5FF] bg-[linear-gradient(180deg,#FFFFFF_0%,#FCFBFF_100%)] transition hover:border-[#D8CCFF] hover:shadow-[0_20px_34px_-24px_rgba(124,58,237,0.4)]">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-black text-slate-900">{order.course?.title || 'سفارش دوره'}</p>
                  <p className="mt-1 text-xs text-slate-500">{formatPersianDateWithTime(order.createdAt)}</p>
                </div>
                <div className="text-left">
                  <p className="text-sm font-black text-[#1C3B9B]">{Number(order.amount || 0).toLocaleString('fa-IR')} تومان</p>
                  <span className={`mt-1 inline-flex rounded-full border px-2 py-0.5 text-[11px] font-bold ${resolveOrderStatus(order.status).className}`}>
                    {resolveOrderStatus(order.status).label}
                  </span>
                </div>
              </div>
            </DashboardCard>
          ))}
        </div>
      ) : (
        <DashboardEmptyState title="هنوز سفارشی ثبت نشده" subtitle="پس از اضافه‌کردن یا خرید دوره‌ها، تاریخچه سفارش اینجا نمایش داده می‌شود." />
      )}
    </div>
  );
};
